import React, { useState, useEffect } from 'react';
import { mktFetch, MARKETING_API } from './MarketingHubModal';

const API = MARKETING_API;
const PROMPT_MAX = 1200;
// Wan 2.2 renders ~5 s of video in 5–20 min on the Spark — poll for up to 45 min.
const POLL_INTERVAL_MS = 5000;
const POLL_MAX_TICKS = 540;

async function parseMktJson(res) {
  const text = await res.text();
  try {
    return { data: JSON.parse(text), ok: res.ok, status: res.status };
  } catch {
    if (text.trimStart().startsWith('<')) {
      throw new Error(
        'The server returned an HTML error page (often a proxy timeout). Video generation runs in the background — check Studio + Review in a few minutes.'
      );
    }
    throw new Error(text.slice(0, 240) || `HTTP ${res.status}`);
  }
}

async function pollMediaRun(runId, onProgress) {
  const pollOnce = async (elapsed) => {
    const runRes = await mktFetch(`${API}/platform/agents/runs/${runId}`);
    const { data: runData } = await parseMktJson(runRes);
    const run = runData.run;
    const stats = typeof run?.stats === 'string' ? JSON.parse(run.stats) : (run?.stats || {});
    onProgress?.({
      elapsed,
      status: run?.status,
      phase: stats.phase,
      engine: stats.engine,
    });
    return run;
  };

  let run = await pollOnce(0);
  if (run?.status === 'completed') {
    return typeof run.stats === 'string' ? JSON.parse(run.stats) : (run.stats || {});
  }
  if (run?.status === 'failed') throw new Error(run.error_message || 'Video generation failed');

  for (let i = 1; i < POLL_MAX_TICKS; i++) {
    await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
    run = await pollOnce(Math.round((i * POLL_INTERVAL_MS) / 1000));
    const stats = typeof run?.stats === 'string' ? JSON.parse(run.stats) : (run?.stats || {});
    if (run?.status === 'completed') return stats;
    if (run?.status === 'failed') throw new Error(run.error_message || 'Video generation failed');
  }
  throw new Error('Still rendering — check Studio + Review later; the video will attach to the draft when done.');
}

function phaseLabel(phase) {
  if (phase === 'drafting_prompt') return 'Qwen is writing the video prompt…';
  if (phase === 'loading_model') return 'Preparing…';
  if (phase === 'rendering') return 'Wan 2.2 rendering video on GPU (5–20 min for ~5 s of footage)…';
  if (phase === 'saving') return 'Saving video and attaching to draft…';
  return 'Wan 2.2 rendering video on GPU (5–20 min for ~5 s of footage)…';
}

function parseMeta(draft) {
  if (!draft) return {};
  if (typeof draft.metadata === 'string') {
    try { return JSON.parse(draft.metadata); } catch { return {}; }
  }
  return draft.metadata || {};
}

function fmtElapsed(sec) {
  if (sec < 60) return `${sec}s`;
  return `${Math.floor(sec / 60)}m ${sec % 60}s`;
}

export default function MarketingVideoPromptModal({ draft, onClose, onGenerated }) {
  const [videoPrompt, setVideoPrompt] = useState('');
  const [negativePrompt, setNegativePrompt] = useState('');
  const [previewMode, setPreviewMode] = useState('');
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [cacheNote, setCacheNote] = useState('');
  const [progress, setProgress] = useState('');
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (busy !== 'generating') {
      setElapsed(0);
      return undefined;
    }
    const t0 = Date.now();
    const tick = setInterval(() => setElapsed(Math.floor((Date.now() - t0) / 1000)), 1000);
    return () => clearInterval(tick);
  }, [busy]);

  useEffect(() => {
    if (!draft?.id) return;
    let cancelled = false;

    const meta = parseMeta(draft);
    const cached = meta.video_draft || meta.video;
    if (cached?.video_prompt) {
      setVideoPrompt(cached.video_prompt);
      setNegativePrompt(cached.negative_prompt || '');
      setPreviewMode(cached.preview_mode || '');
      setCacheNote('Loaded saved prompt — click REDRAFT PROMPT for a fresh Qwen draft.');
      return undefined;
    }

    (async () => {
      setBusy('drafting');
      setErr('');
      setCacheNote('');
      try {
        const res = await mktFetch(`${API}/platform/drafts/${draft.id}/draft-video-prompt`, {
          method: 'POST',
          body: JSON.stringify({}),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Could not draft prompt');
        if (cancelled) return;
        setVideoPrompt(data.video_prompt || '');
        setNegativePrompt(data.negative_prompt || '');
        setPreviewMode(data.preview_mode || '');
      } catch (e) {
        if (!cancelled) setErr(e.message);
      } finally {
        if (!cancelled) setBusy('');
      }
    })();

    return () => { cancelled = true; };
  }, [draft?.id]);

  if (!draft) return null;

  const generateVideo = async () => {
    if (!videoPrompt.trim()) {
      setErr('Video prompt cannot be empty.');
      return;
    }
    setBusy('generating');
    setErr('');
    setProgress('Starting video generation…');
    try {
      const res = await mktFetch(`${API}/platform/drafts/${draft.id}/generate-media`, {
        method: 'POST',
        body: JSON.stringify({
          kind: 'video',
          video_prompt: videoPrompt.trim(),
          negative_prompt: negativePrompt.trim(),
        }),
      });
      const { data, ok } = await parseMktJson(res);
      if (!ok) throw new Error(data.error || 'Generation failed');
      let result = data;
      if (data.run_id && (data.started || data.already_running)) {
        setProgress(data.message || 'Generating video…');
        result = await pollMediaRun(data.run_id, ({ elapsed: sec, phase, engine }) => {
          setProgress(`${phaseLabel(phase)} ${fmtElapsed(sec)}${engine ? ` · ${engine}` : ''}`);
        });
      }
      onGenerated?.(result);
      onClose();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy('');
      setProgress('');
    }
  };

  const redraftPrompt = async () => {
    setBusy('drafting');
    setErr('');
    setCacheNote('');
    try {
      const res = await mktFetch(`${API}/platform/drafts/${draft.id}/draft-video-prompt`, {
        method: 'POST',
        body: JSON.stringify({ refresh: true }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Redraft failed');
      setVideoPrompt(data.video_prompt || '');
      setNegativePrompt(data.negative_prompt || '');
      setPreviewMode(data.preview_mode || '');
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy('');
    }
  };

  return (
    <div
      className="mkt-preview-backdrop"
      role="presentation"
      onClick={busy ? undefined : onClose}
    >
      <div className="mkt-preview-modal mkt-graphic-prompt-modal" role="dialog" onClick={(e) => e.stopPropagation()}>
        <div className="mkt-preview-modal-head">
          <h4>Video prompt · {draft.platform}{previewMode ? ` · ${previewMode}` : ''}</h4>
          <button
            type="button"
            className="blog-tactical-action-btn blog-tactical-action-delete"
            disabled={busy === 'generating'}
            onClick={onClose}
          >
            CLOSE
          </button>
        </div>
        <p className="marketing-panel-desc">
          Qwen drafts a cinematic Wan 2.2 prompt from the story. Edit it, then generate — the model renders ~5 seconds of video and it can take 5–20 minutes on the Spark. Do not ask for on-screen text or logos; video models render text badly.
        </p>
        {busy === 'generating' && (
          <div className="mkt-graphic-generating-banner" role="status" aria-live="polite">
            <span className="mkt-graphic-spinner" aria-hidden="true" />
            <div className="mkt-graphic-generating-text">
              <strong>{progress || 'GENERATING VIDEO…'}</strong>
              <span className="marketing-source-meta">
                Wan 2.2 video model · {fmtElapsed(elapsed)} elapsed · safe to close — the video attaches to the draft when done
              </span>
            </div>
          </div>
        )}
        {busy === 'drafting' && <p className="marketing-msg">Drafting video prompt with Qwen 7B…</p>}
        {cacheNote && !busy && <p className="marketing-source-meta">{cacheNote}</p>}
        <label className="marketing-integration-label">
          Video prompt
          <span className="marketing-prompt-char-count">{videoPrompt.length} / {PROMPT_MAX}</span>
          <textarea
            rows={8}
            value={videoPrompt}
            disabled={busy === 'drafting'}
            onChange={(e) => setVideoPrompt(e.target.value.slice(0, PROMPT_MAX))}
          />
        </label>
        <label className="marketing-integration-label">
          Negative prompt
          <textarea
            rows={3}
            value={negativePrompt}
            disabled={busy === 'drafting'}
            onChange={(e) => setNegativePrompt(e.target.value)}
          />
        </label>
        {progress && busy === 'generating' && <p className="marketing-msg mkt-graphic-progress-detail">{progress}</p>}
        {err && <p className="marketing-msg-err">{err}</p>}
        <div className="marketing-form-actions">
          <button
            type="button"
            className="blog-tactical-action-btn blog-tactical-action-edit"
            disabled={!!busy}
            onClick={generateVideo}
          >
            {busy === 'generating' ? (progress || 'GENERATING…') : 'GENERATE VIDEO'}
          </button>
          <button
            type="button"
            className="blog-tactical-action-btn blog-tactical-action-view"
            disabled={!!busy}
            onClick={redraftPrompt}
          >
            REDRAFT PROMPT
          </button>
        </div>
      </div>
    </div>
  );
}
